import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";

import { API_URL, HASH_API_TOKEN } from "config";
import { managedFetch } from "utils/FetchManager";
import usePage from "hooks/usePage";
import { useAuth } from "contexts/AuthContext";

const RunsContext = createContext();

export const useRuns = () => useContext(RunsContext);

const FilterTypeMap = {
  urban: "intermix",
  vegetation: "mtt",
};

export const RunsProvider = ({ children }) => {
  const [isSimulating, setIsSimulating] = useState(false);
  const [searchParams] = useSearchParams();
  const { projectId, page, isSharePage } = usePage();
  const { currentUser } = useAuth();
  const prefix = isSharePage ? "/hash" : "";
  const AUTH_TOKEN = currentUser?.accessToken;
  const BEARER_TOKEN = isSharePage ? HASH_API_TOKEN : AUTH_TOKEN;
  const projectFilter = projectId
    ? isSharePage
      ? `filter_project_hash=${projectId}`
      : `filter_project_id=${projectId}`
    : "";

  const [runs, setRuns] = useState([]);

  const [scenarioId, setScenarioId] = useState(
    parseInt(searchParams.get("scenario_id")) || null
  );

  const [isRunLoading, setIsRunLoading] = useState(false);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);

  const [newRunName, setNewRunName] = useState("");
  const [newRunDescription, setNewRunDescription] = useState("");

  const [hasMoreRuns, setHasMoreRuns] = useState(true);

  const [isCreatingSimulation, setIsCreatingSimulation] = useState(false);

  const [intermixResults, setIntermixResults] = useState(null);

  const [intermixSymbolKey, setIntermixSymbolKey] = useState("ignited");

  const [intermixRunID, setIntermixRunID] = useState(null);
  const [pathwaysRunID, setPathwaysRunID] = useState(null);

  const [selectedIntermixRunIndex, setSelectedIntermixRunIndex] = useState(1);
  const [intermixRunMetadata, setIntermixRunMetadata] = useState(null);

  const [annotations, setAnnotations] = useState(null);

  const [intermixScenarioTree, setIntermixScenarioTree] = useState(null);
  const [intermixScenarioEmbers, setIntermixScenarioEmbers] = useState(null);
  const [intermixScenarioSequence, setIntermixScenarioSequence] =
    useState(null);
  const [intermixRunsList, setIntermixRunsList] = useState(null);

  // Input geometries attached to the run (mitigations, structure hardening,
  // additional buildings). They are not part of the model output, so they come
  // from their own endpoints rather than the composite result.
  const [intermixMitigations, setIntermixMitigations] = useState(null);
  const [intermixStructureHardening, setIntermixStructureHardening] =
    useState(null);
  const [intermixAdditionalBuildings, setIntermixAdditionalBuildings] =
    useState(null);

  const [isLoadingIntermixSummary, setIsLoadingIntermixSummary] =
    useState(false);
  const [isLoadingIntermixExtended, setIsLoadingIntermixExtended] =
    useState(false);

  const runsFetchLimit = 100;
  const filterType = FilterTypeMap[page];

  const fetchRecentRuns = useCallback(async () => {
    if (!projectFilter || !filterType) {
      return;
    }
    setHasMoreRuns(true);
    setIsLoadingRuns(true);
    const response = await managedFetch(
      `${API_URL}${prefix}/simulation?limit=${runsFetchLimit}&filter_type=${filterType}&${projectFilter}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${BEARER_TOKEN}`,
          "Content-Type": "application/json",
        },
      },
      isSharePage ? null : currentUser
    );
    const data = await response.json();
    setIsLoadingRuns(false);
    return data;
  }, [
    runsFetchLimit,
    projectFilter,
    filterType,
    prefix,
    BEARER_TOKEN,
    currentUser,
    isSharePage,
  ]);

  const fetchMoreRuns = useCallback(async () => {
    if (!projectFilter || !filterType) {
      return;
    }
    setIsLoadingRuns(true);
    const offset = runs.length;

    try {
      const response = await managedFetch(
        `${API_URL}${prefix}/simulation?offset=${offset}&limit=${runsFetchLimit}&filter_type=${filterType}&${projectFilter}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${BEARER_TOKEN}`,
            "Content-Type": "application/json",
          },
        },
        isSharePage ? null : currentUser
      );
      const newRuns = await response.json();
      setIsLoadingRuns(false);

      if (
        !newRuns ||
        !newRuns.simulations ||
        newRuns.simulations.length === 0
      ) {
        setHasMoreRuns(false);
        return;
      }

      if (newRuns.simulations.length < runsFetchLimit) {
        setHasMoreRuns(false); // No more runs to fetch
      }

      setRuns((prevRuns) => [...prevRuns, ...newRuns.simulations]);
    } catch (err) {
      console.error("Error fetching more runs:", err);
      setHasMoreRuns(false);
    }
  }, [
    runs,
    projectFilter,
    filterType,
    prefix,
    BEARER_TOKEN,
    currentUser,
    isSharePage,
  ]);

  function deleteRun(runId) {
    managedFetch(
      `${API_URL}/simulation/${runId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${AUTH_TOKEN}`,
          "Content-Type": "application/json",
        },
      },
      currentUser
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to delete run");
        }
        setRuns((prevRuns) => prevRuns.filter((run) => run.id !== runId));
        return response.json();
      })
      .catch((error) => {
        console.error("Error deleting run:", error);
      });
  }

  function patchRun(runId, key, value) {
    const body = { [key]: value };
    managedFetch(
      `${API_URL}/simulation/${runId}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${AUTH_TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
      currentUser
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to update run");
        }
        reloadRecentRuns(); // Reload runs after patching
        return response.json();
      })
      .catch((error) => {
        console.error("Error updating run:", error);
      });
  }

  // Create a function to manually reload recent runs
  const reloadRecentRuns = useCallback(async () => {
    try {
      const fetchedRuns = await fetchRecentRuns();
      fetchedRuns && setRuns(fetchedRuns.simulations);
      return true; // Return success status
    } catch (err) {
      console.error("Error reloading recent runs:", err);
      return false; // Return failure status
    }
  }, [fetchRecentRuns]);
  // Load recent runs
  const contextKeyRef = useRef(null);
  useEffect(() => {
    // Only clear the list when the project/type context actually changes
    // between stable values. Navigating to a run URL transiently nulls
    // projectId, which we want to ignore here.
    if (projectFilter && filterType) {
      const key = `${projectFilter}:${filterType}`;
      if (contextKeyRef.current && contextKeyRef.current !== key) {
        setRuns([]);
      }
      contextKeyRef.current = key;
    }

    const fetchRuns = async () => {
      try {
        const fetchedRuns = await fetchRecentRuns();
        if (!fetchedRuns?.simulations) return;
        const byId = Object.fromEntries(
          fetchedRuns.simulations.map((r) => [r.id, r])
        );
        setRuns((prev) =>
          prev.length === 0
            ? fetchedRuns.simulations
            : prev.map((r) => byId[r.id] ?? r)
        );
      } catch (err) {
        console.error("Error fetching recent runs:", err);
      }
    };
    fetchRuns();
    const interval = setInterval(fetchRuns, 30000);
    return () => clearInterval(interval);
  }, [fetchRecentRuns, projectFilter, filterType]);

  return (
    <RunsContext.Provider
      value={{
        runs,
        setRuns,
        isRunLoading,
        setIsRunLoading,
        isSimulating,
        setIsSimulating,
        isLoadingRuns,

        newRunName,
        setNewRunName,
        newRunDescription,
        setNewRunDescription,
        fetchMoreRuns,
        hasMoreRuns,

        deleteRun,
        patchRun,

        isCreatingSimulation,
        setIsCreatingSimulation,
        fetchRecentRuns,
        reloadRecentRuns,

        intermixResults,
        setIntermixResults,

        intermixSymbolKey,
        setIntermixSymbolKey,

        intermixScenarioTree,
        setIntermixScenarioTree,

        intermixScenarioEmbers,
        setIntermixScenarioEmbers,

        intermixScenarioSequence,
        setIntermixScenarioSequence,

        intermixRunsList,
        setIntermixRunsList,

        intermixMitigations,
        setIntermixMitigations,

        intermixStructureHardening,
        setIntermixStructureHardening,

        intermixAdditionalBuildings,
        setIntermixAdditionalBuildings,

        intermixRunID,
        setIntermixRunID,

        selectedIntermixRunIndex,
        setSelectedIntermixRunIndex,
        intermixRunMetadata,
        setIntermixRunMetadata,
        pathwaysRunID,
        setPathwaysRunID,

        annotations,
        setAnnotations,

        scenarioId,
        setScenarioId,

        isLoadingIntermixSummary,
        setIsLoadingIntermixSummary,

        isLoadingIntermixExtended,
        setIsLoadingIntermixExtended,
      }}
    >
      {children}
    </RunsContext.Provider>
  );
};
